import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["id", "options", "disabled", "selectedItemsPlaceholder", "prefixWithSelectedItemsPlaceholder", "referenceIndicator", "label", "referenceHoverContent", "supportText", "error", "open", "autoUpdateMenuPosition", "multi", "menuInlineSize", "menuMaxBlockSize", "searchable", "clearable", "textInputPlaceholder", "noResultsText", "referenceSize", "placement", "inputValue", "selectedValues", "onSelectedOptionChange", "onInputValueChange", "onOpenChange", "loadAsyncOptions", "referenceStyles", "referenceVariant", "clearButtonAriaLabel"],
  _excluded2 = ["hovered"];
/* eslint-disable @typescript-eslint/no-shadow */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useFloating, useListNavigation, useTypeahead, useClick, useDismiss, useRole, useInteractions, offset, flip, size, hide, autoUpdate, FloatingPortal, FloatingFocusManager } from '@floating-ui/react';
import { RemoveIcon, SearchIcon, useIconSize } from 'foundations-assets';
import { View } from '../View/View';
import { Flex } from '../Flex/Flex';
import { LoadingSpinner } from '../../transitional/components/LoadingSpinner';
import { DownCarat } from './DownCarat';
import { OptionContent } from './OptionContent';
import { NoResults } from './NoResults';
import { useControlledOpen } from '../../hooks/useControlledOpen';
import { useControlledInputValue, useControlledSelectedValues, useAsyncOptions, useReferenceContent } from './Select.hooks';
import { StyledContainer, StyledMenu, StyledMenuOption, StyledPlaceholder, StyledRemoveIconWrapper, StyledTextInput, StyledTextInputWrapper, StyledReference, StyledReferenceIndicator, StyledReferenceLabelWrapper, StyledReferenceSupportText, StyledReferenceWrapper, StyledMenuOptionsWrapper, StyledReferenceContainer } from './Select.styles';
import devLogger from 'react-utils/devLogger';
import { filterProps } from '../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../hooks/useMapTokenPropsToCSS';
import unescapedText from 'I18n/utils/unescapedText';
import { useHovered } from '../../transitional/utils/useHovered';
import { getFlattenedOptions, normalizeText } from './Select.helpers';
import { Text } from '../../components/Text';
import { uniqueId } from '../../transitional/utils/uniqueId';
import { getDefaultNode } from '../../transitional/utils/RootNode';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const Select = _ref => {
  var _middlewareData$hide;
  let {
      id,
      options: optionsProp = [],
      disabled,
      selectedItemsPlaceholder: selectedItemsPlaceholderProp,
      prefixWithSelectedItemsPlaceholder = false,
      referenceIndicator: referenceIndicatorProp,
      label,
      referenceHoverContent,
      supportText,
      error = false,
      open: controlledOpen,
      autoUpdateMenuPosition = true,
      multi = false,
      menuInlineSize,
      menuMaxBlockSize = 285,
      searchable = false,
      clearable = false,
      textInputPlaceholder = unescapedText('foundations-components.Select.searchPlaceholder'),
      noResultsText = unescapedText('foundations-components.Select.noResults'),
      referenceSize = 'default',
      placement = 'bottom-start',
      inputValue: controlledInputValue,
      selectedValues: controlledSelectedValues,
      onSelectedOptionChange,
      onInputValueChange,
      onOpenChange,
      loadAsyncOptions,
      referenceStyles,
      referenceVariant = 'default',
      clearButtonAriaLabel = unescapedText('foundations-components.Select.clearSelections')
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (process.env.NODE_ENV !== 'production') {
    if (multi && referenceIndicatorProp) {
      devLogger.warn({
        message: 'Select: referenceIndicator should not be used in multi-select mode as it renders the total number of selected items in the reference element',
        key: 'Select: no referenceIndicator with multi-select'
      });
    }
  }
  const _id = useMemo(() => uniqueId('select-'), []);
  const {
    tokenProps
  } = filterProps(referenceStyles || {});
  const marginStyles = useMapTokenPropsToCSS(tokenProps);
  const {
    domProps
  } = filterProps(rest);
  const {
    isOpen,
    setIsOpen
  } = useControlledOpen({
    controlledOpen,
    onOpenChange
  });
  const {
    selectedValues,
    handleSelect: hookHandleSelect
  } = useControlledSelectedValues({
    controlledSelectedValues,
    onSelectedOptionChange,
    multi
  });
  const {
    inputValue,
    handleInputValueChange
  } = useControlledInputValue({
    controlledInputValue,
    onInputValueChange
  });
  const {
    isLoading,
    error: loadOptionsError,
    options: asyncOptions,
    fetchOptions
  } = useAsyncOptions({
    loadAsyncOptions
  });
  const [activeIndex, setActiveIndex] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const isAsyncSelect = searchable && loadAsyncOptions !== undefined;
  const rawOptions = isAsyncSelect ? asyncOptions : optionsProp;
  const options = getFlattenedOptions(rawOptions);
  const {
    referenceText,
    referenceIndicator,
    referenceLayout
  } = useReferenceContent({
    options,
    selectedValues,
    multi,
    referenceIndicator: referenceIndicatorProp,
    selectedItemPlaceholder: selectedItemsPlaceholderProp,
    prefixWithSelectedItemsPlaceholder
  });
  useEffect(() => {
    if (!multi && selectedValues.length === 1) {
      const index = options.findIndex(({
        value
      }) => value === selectedValues[0]);
      setSelectedIndex(index);
    }
  }, [multi, selectedValues, options]);
  useEffect(() => {
    if (isAsyncSelect && (inputValue === '' || inputValue)) {
      fetchOptions(inputValue);
    }
  }, [isAsyncSelect, inputValue, fetchOptions]);
  const handleOpenChange = useCallback(open => {
    setIsOpen(open);
    if (!open && searchable) {
      handleInputValueChange('');
    }
  }, [setIsOpen, searchable, handleInputValueChange]);
  const listRef = useRef([]);
  const listContentRef = useRef([]);
  const isTypingRef = useRef(false);
  const searchInputRef = useRef(null);
  const {
    refs,
    context,
    floatingStyles,
    middlewareData
  } = useFloating({
    placement,
    open: isOpen,
    onOpenChange: handleOpenChange,
    whileElementsMounted: autoUpdateMenuPosition ? autoUpdate : undefined,
    middleware: [offset(5), flip({
      padding: 10
    }), size({
      apply({
        rects,
        elements
      }) {
        Object.assign(elements.floating.style, {
          maxBlockSize: `${menuMaxBlockSize}px`,
          inlineSize: `${menuInlineSize !== null && menuInlineSize !== void 0 ? menuInlineSize : rects.reference.width}px`
        });
      },
      padding: 10
    }), hide()]
  });
  const click = useClick(context, {
    event: 'mousedown'
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, {
    role: 'listbox'
  });
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    selectedIndex,
    onNavigate: setActiveIndex,
    loop: true,
    virtual: true,
    focusItemOnOpen: true,
    focusItemOnHover: false
  });
  const typeahead = useTypeahead(context, {
    listRef: listContentRef,
    activeIndex,
    onMatch: setActiveIndex,
    enabled: !searchable,
    onTypingChange(isTyping) {
      isTypingRef.current = isTyping;
    }
  });
  const {
    getReferenceProps,
    getFloatingProps,
    getItemProps
  } = useInteractions([dismiss, role, listNav, typeahead, click]);
  const _useHovered = useHovered({}),
    {
      hovered
    } = _useHovered,
    onMouseEventHandlers = _objectWithoutPropertiesLoose(_useHovered, _excluded2);
  const displayedOptions = useMemo(() => {
    // Disable the default, internal options filtering mechanism
    if (isAsyncSelect || !searchable || inputValue === '') {
      return options;
    }
    const normalizedSearchQuery = normalizeText(inputValue);
    return options.filter(({
      text,
      supportText,
      disabled
    }) => {
      const textMatch = normalizeText(text).includes(normalizedSearchQuery);
      const supportTextMatch = supportText ? normalizeText(supportText).includes(normalizedSearchQuery) : undefined;
      return !disabled && (textMatch || supportTextMatch);
    });
  }, [options, inputValue, searchable, isAsyncSelect]);
  const handleSelectOption = useCallback(({
    text,
    value
  }) => {
    hookHandleSelect(text, value);
    if (!multi) {
      setIsOpen(false);
      if (searchable || loadAsyncOptions) {
        handleInputValueChange('');
      }
    }
  }, [handleInputValueChange, hookHandleSelect, loadAsyncOptions, multi, searchable, setIsOpen]);
  const handleClearSelectedOptions = useCallback(({
    event,
    eventType
  }) => {
    event.preventDefault();
    const clearSelections = () => {
      setActiveIndex(null);
      setIsOpen(false);
      hookHandleSelect(null, null);
    };
    if (eventType === 'mouse') {
      clearSelections();
    } else {
      const e = event;
      if (e.key === 'Enter' || e.key === ' ') {
        clearSelections();
      } else if (e.key === 'Tab') {
        if (e.shiftKey) {
          var _refs$domReference$cu;
          // Move focus back to the select element
          (_refs$domReference$cu = refs.domReference.current) === null || _refs$domReference$cu === void 0 || _refs$domReference$cu.focus();
        } else {
          if (isOpen) {
            if (searchable) {
              var _searchInputRef$curre;
              (_searchInputRef$curre = searchInputRef.current) === null || _searchInputRef$curre === void 0 || _searchInputRef$curre.focus();
            } else {
              var _listRef$current$;
              (_listRef$current$ = listRef.current[0]) === null || _listRef$current$ === void 0 || _listRef$current$.focus();
            }
          } else {
            var _refs$domReference$cu2;
            (_refs$domReference$cu2 = refs.domReference.current) === null || _refs$domReference$cu2 === void 0 || _refs$domReference$cu2.focus();
          }
        }
      }
    }
  }, [hookHandleSelect, isOpen, refs.domReference, searchable, setIsOpen]);
  const handleOptionKeyDown = useCallback(event => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ' && !searchable && !isTypingRef.current) {
      event.preventDefault();
      const activeOption = displayedOptions[activeIndex];
      if (activeOption) {
        handleSelectOption({
          text: activeOption.text,
          value: activeOption.value
        });
      }
    }
  }, [activeIndex, disabled, displayedOptions, handleSelectOption, searchable]);

  /**
   * Set the active index when the up/down arrow keys are used to navigate
   * the list of options while `searchable` mode is enabled and the focus
   * remains on the search input.
   */
  const handleSearchInputKeyDown = useCallback(event => {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      const lastIndex = displayedOptions.length - 1;
      let newIndex;
      if (event.key === 'ArrowDown') {
        if (activeIndex === null || activeIndex === lastIndex) {
          newIndex = 0;
        } else {
          newIndex = activeIndex + 1;
        }
      } else {
        if (activeIndex === null || activeIndex === 0) {
          newIndex = lastIndex;
        } else {
          newIndex = activeIndex - 1;
        }
      }
      setActiveIndex(newIndex);
    } else if (event.key === 'Enter' && activeIndex !== null) {
      event.preventDefault();
      const selectedOption = displayedOptions[activeIndex];
      if (selectedOption) {
        handleSelectOption({
          text: selectedOption.text,
          value: selectedOption.value
        });
      }
    }
  }, [activeIndex, displayedOptions, handleSelectOption]);
  const hasSelectedValues = selectedValues.length > 0;
  const renderMenu = !isLoading && !loadOptionsError && displayedOptions.length > 0;
  const removeIconSize = useIconSize('100');
  const textInputIconSize = useIconSize('400');
  const referenceProps = getReferenceProps();

  // Prevent the event from bubbling up to the parent element when the escape key is pressed
  // TODO we probably need a better long term solution to this type of problem
  referenceProps.onKeyDown = event => {
    if (event.key === 'Escape') {
      event.stopPropagation();
    }
  };
  return /*#__PURE__*/_jsxs(StyledContainer, {
    children: [/*#__PURE__*/_jsxs(StyledReferenceContainer, Object.assign({
      $marginStyles: marginStyles
    }, onMouseEventHandlers, {
      children: [label && /*#__PURE__*/_jsxs(StyledReferenceLabelWrapper, {
        id: _id,
        onClick: () => {
          var _refs$domReference$cu3;
          return (_refs$domReference$cu3 = refs.domReference.current) === null || _refs$domReference$cu3 === void 0 ? void 0 : _refs$domReference$cu3.focus();
        },
        children: [/*#__PURE__*/_jsx(Text, {
          typography: "heading-50",
          truncate: true,
          children: label
        }), hovered && referenceHoverContent]
      }), /*#__PURE__*/_jsxs(StyledReferenceWrapper, {
        $referenceSize: referenceSize,
        children: [/*#__PURE__*/_jsxs(StyledReference, Object.assign({
          $referenceVariant: referenceVariant,
          $error: error,
          "aria-labelledby": label ? _id : undefined,
          disabled: disabled,
          id: id,
          ref: refs.setReference,
          type: "button"
        }, selectedValues.length > 0 && {
          'data-selected-values': selectedValues.join(',')
        }, referenceProps, domProps, {
          children: [/*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            overflow: "hidden",
            gap: referenceLayout === 'icon-text' ? '200' : '0',
            children: [referenceIndicator && /*#__PURE__*/_jsx(StyledReferenceIndicator, {
              $referenceSize: referenceSize,
              children: referenceIndicator
            }), referenceText && /*#__PURE__*/_jsx(StyledPlaceholder, {
              $hasSelectedValues: hasSelectedValues,
              children: referenceText
            })]
          }), /*#__PURE__*/_jsx(DownCarat, {
            hasSelectedValues: hasSelectedValues,
            clearable: clearable
          })]
        })), clearable && hasSelectedValues && /*#__PURE__*/_jsx(StyledRemoveIconWrapper, {
          role: "button",
          tabIndex: 0,
          "aria-label": clearButtonAriaLabel,
          onClick: event => {
            handleClearSelectedOptions({
              eventType: 'mouse',
              event
            });
          },
          onKeyDown: event => {
            handleClearSelectedOptions({
              eventType: 'keyboard',
              event
            });
          },
          $size: removeIconSize,
          children: /*#__PURE__*/_jsx(RemoveIcon, {})
        })]
      }), supportText && /*#__PURE__*/_jsx(StyledReferenceSupportText, {
        $error: error,
        children: supportText
      })]
    })), isOpen && /*#__PURE__*/_jsx(FloatingPortal, {
      root: getDefaultNode(),
      children: /*#__PURE__*/_jsx(FloatingFocusManager, {
        context: context,
        modal: false,
        returnFocus: true,
        children: /*#__PURE__*/_jsxs(StyledMenu, Object.assign({
          $isReferenceHidden: (_middlewareData$hide = middlewareData.hide) === null || _middlewareData$hide === void 0 ? void 0 : _middlewareData$hide.referenceHidden,
          ref: refs.setFloating,
          style: floatingStyles,
          "aria-multiselectable": multi
        }, getFloatingProps(), {
          "data-tinymce-floating-element": "true",
          children: [(searchable || isAsyncSelect) && /*#__PURE__*/_jsxs(StyledTextInputWrapper, {
            $iconSize: textInputIconSize,
            children: [/*#__PURE__*/_jsx(SearchIcon, {}), /*#__PURE__*/_jsx(StyledTextInput, {
              type: "text",
              placeholder: textInputPlaceholder,
              ref: searchInputRef,
              value: inputValue,
              onChange: event => handleInputValueChange(event.target.value),
              onKeyDown: handleSearchInputKeyDown
            })]
          }), /*#__PURE__*/_jsxs(StyledMenuOptionsWrapper, {
            $searchable: searchable,
            children: [renderMenu && displayedOptions.map(({
              text,
              value,
              disabled,
              supportText,
              indicator,
              isSubheading,
              divider
            }, index) => {
              const isSelected = selectedValues.includes(value);
              const isActive = activeIndex === index;
              const isValidOption = !disabled && !isSubheading;
              return /*#__PURE__*/_jsx(StyledMenuOption, Object.assign({
                $isActive: isActive,
                $disabled: disabled,
                $isSubheading: isSubheading,
                $divider: divider,
                "data-text": text,
                "data-value": value,
                ref: node => {
                  if (isValidOption) {
                    listRef.current[index] = node;
                    listContentRef.current[index] = text;
                  }
                },
                role: "option",
                tabIndex: isActive ? 0 : -1,
                "aria-selected": isSelected,
                "aria-disabled": disabled
              }, getItemProps({
                onClick() {
                  if (isValidOption) handleSelectOption({
                    text,
                    value
                  });
                },
                onKeyDown(event) {
                  handleOptionKeyDown(event);
                },
                onPointerEnter() {
                  if (isValidOption) {
                    setActiveIndex(index);
                  }
                },
                onPointerLeave() {
                  setActiveIndex(null);
                }
              }), {
                children: /*#__PURE__*/_jsx(OptionContent, {
                  multi: multi,
                  isSelected: isSelected,
                  disabled: disabled,
                  indicator: indicator,
                  text: text,
                  supportText: supportText,
                  inputValue: inputValue,
                  isSubheading: isSubheading
                })
              }), value);
            }), isAsyncSelect && isLoading && /*#__PURE__*/_jsx(LoadingSpinner, {
              layout: "centered",
              size: "small",
              marginBlock: "300"
            })]
          }), /*#__PURE__*/_jsx(NoResults, {
            isLoading: isLoading,
            error: loadOptionsError,
            totalOptions: displayedOptions.length,
            noResultsText: noResultsText
          }), loadOptionsError && /*#__PURE__*/_jsx(View, {
            paddingBlock: "200",
            paddingInline: "300",
            children: loadOptionsError
          })]
        }))
      })
    })]
  });
};